import React, { useEffect, useRef, useState } from "react";
import {
  Check,
  ChevronDown,
  ChevronUp,
  Paperclip,
  Pencil,
  Plus,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { PdfTypeIcon, Player, VideoTypeIcon } from "../AdminIcons";
import {
  useCreateCourseModuleMutation,
  useGetCourseModulesQuery,
  useUploadCourseLectureMutation,
  useUploadCoursePdfMutation,
  useUpdateCourseModuleMutation,
} from "../../services/course/courseApi";
import { useDeleteCourseModuleMutation } from "../../services/deletion-history/deletionHistoryApi";
import Loader from "../common/Loader";
import { showToast } from "../../utils/toast";

const CourseModulesSection = ({ courseId, courseData }) => {
  const isLiveCourse =
    String(courseData?.courseEnrollementType || "").toLowerCase() === "live";

  const [moduleItems, setModuleItems] = useState([]);
  const [isMaterialModalOpen, setIsMaterialModalOpen] = useState(false);
  const [materialFiles, setMaterialFiles] = useState([]);
  const [materialError, setMaterialError] = useState("");
  const [activeModuleId, setActiveModuleId] = useState("");
  const [hasUploadedPdf, setHasUploadedPdf] = useState(false);
  const [errorsById, setErrorsById] = useState({});
  const [originalById, setOriginalById] = useState({});
  const uploadTimerRef = useRef(null);
  const materialInputRef = useRef(null);
  const [materialUploadType, setMaterialUploadType] = useState("");
  const [lastMaterialType, setLastMaterialType] = useState("");
  const [isUploadingMaterials, setIsUploadingMaterials] = useState(false);
  const [uploadCoursePdf, { isLoading: isUploadingPdf }] =
    useUploadCoursePdfMutation();
  const [uploadCourseLecture, { isLoading: isUploadingLecture }] =
    useUploadCourseLectureMutation();
  const { data: modulesResponse } = useGetCourseModulesQuery(courseId, {
    skip: !courseId,
  });
  useEffect(() => {
    if (!courseId) return;

    const raw = modulesResponse?.data;
    if (!raw || raw.length === 0) {
      return;
    }

    const mapped = raw.map((m, idx) => {
      const completed = Boolean(m.completed === true);

      return {
        id: m._id || idx + 1,
        courseId: m.moduleCourse,
        title: m.moduleName || "",

        status: completed ? "Complete" : "Incomplete",
        statusClassName: completed
          ? "bg-[#DCFCE7] text-[#166534]"
          : "bg-[#FEE2E2] text-[#991B1B]",
        details: {
          description: m.moduleDescription || "",
          sessions: m.noOfSession ?? m.noOfSessions ?? "",
          duration: m.sessionDuration ?? m.duration ?? "",
        },
        isExpanded: false,
      };
    });

    setModuleItems(mapped);
    setOriginalById(
      mapped.reduce((accumulator, module) => {
        accumulator[module.id] = {
          title: module.title,
          description: module.details.description,
          sessions: String(module.details.sessions ?? ""),
          duration: String(module.details.duration ?? ""),
        };
        return accumulator;
      }, {}),
    );
  }, [modulesResponse, courseId]);
  const getModuleStatus = (module) => {
    const hasTitle = module.title.trim() !== "";
    const hasDescription = module.details.description.trim() !== "";
    const hasSessions = String(module.details.sessions).trim() !== "";
    const hasDuration = String(module.details.duration).trim() !== "";

    if (hasTitle && hasDescription && hasSessions && hasDuration) {
      return {
        label: "Complete",
        className: "bg-[#DCFCE7] text-[#166534]",
      };
    }

    return {
      label: "Incomplete",
      className: "bg-[#FEE2E2] text-[#991B1B]",
    };
  };

  const handleTitleChange = (moduleId, nextTitle) => {
    setModuleItems((currentModules) =>
      currentModules.map((module) =>
        module.id === moduleId ? { ...module, title: nextTitle } : module,
      ),
    );
    setErrorsById((current) => {
      if (!current[moduleId]) {
        return current;
      }
      const { [moduleId]: _removed, ...rest } = current;
      return rest;
    });
  };

  const handleModuleToggle = (moduleId) => {
    setModuleItems((currentModules) =>
      currentModules.map((module) =>
        module.id === moduleId
          ? { ...module, isExpanded: !module.isExpanded }
          : module,
      ),
    );
  };

  const handleDetailChange = (moduleId, fieldName, nextValue) => {
    setModuleItems((currentModules) =>
      currentModules.map((module) =>
        module.id === moduleId
          ? {
              ...module,
              details: {
                ...module.details,
                [fieldName]: nextValue,
              },
            }
          : module,
      ),
    );
    setErrorsById((current) => {
      if (!current[moduleId]) {
        return current;
      }
      const { [moduleId]: _removed, ...rest } = current;
      return rest;
    });
  };

  const [createCourseModule, { isLoading: isCreatingModule }] =
    useCreateCourseModuleMutation();
  const [updateCourseModule, { isLoading: isUpdatingModule }] =
    useUpdateCourseModuleMutation();

  const handleAddModule = () => {
    setModuleItems((currentModules) => {
      return [
        ...currentModules,
        {
          id: `new-module-${Date.now()}`,
          title: "",
          status: "Incomplete",
          statusClassName: "bg-[#FEE2E2] text-[#991B1B]",
          details: {
            description: "",
            sessions: "",
            duration: "",
          },
          isExpanded: true,
          isNew: true,
        },
      ];
    });
  };

  const handleSaveModule = async (moduleId) => {
    const module = moduleItems.find((item) => item.id === moduleId);
    if (!module || !courseId) {
      return;
    }

    const titleValue = module.title.trim();
    const descriptionValue = module.details.description.trim();
    const sessionsValue = String(module.details.sessions).trim();
    const durationValue = String(module.details.duration).trim();

    if (!titleValue || !descriptionValue || !sessionsValue || !durationValue) {
      setErrorsById((current) => ({
        ...current,
        [moduleId]: "All fields are required.",
      }));
      return;
    }

    try {
      const response = await createCourseModule({
        courseId,
        moduleName: titleValue,
        moduleDescription: descriptionValue,
        noOfSession: sessionsValue,
        sessionDuration: durationValue,
      }).unwrap();
      const createdId = response?.data?._id;

      setModuleItems((currentModules) =>
        currentModules.map((item) =>
          item.id === moduleId
            ? {
                ...item,
                id: createdId || item.id,
                courseId,
                isNew: false,
              }
            : item,
        ),
      );
      setOriginalById((current) => ({
        ...current,
        [createdId]: {
          title: titleValue,
          description: descriptionValue,
          sessions: sessionsValue,
          duration: durationValue,
        },
      }));
      if (createdId !== moduleId) {
        setOriginalById((current) => {
          const { [moduleId]: _removed, ...rest } = current;
          return rest;
        });
      }
      setErrorsById((current) => {
        if (!current[moduleId]) {
          return current;
        }
        const { [moduleId]: _removed, ...rest } = current;
        return rest;
      });

      showToast({
        message: response?.message || "Module created successfully.",
        type: "success",
      });
    } catch (error) {
      const message =
        error?.data?.message || error?.error || "Failed to create module.";
      setErrorsById((current) => ({
        ...current,
        [moduleId]: message,
      }));
      showToast({ message, type: "error" });
    }
  };

  const handleUpdateModule = async (moduleId) => {
    const module = moduleItems.find((item) => item.id === moduleId);
    if (!module || !courseId || module.isNew) {
      return;
    }

    const titleValue = module.title.trim();
    const descriptionValue = module.details.description.trim();
    const sessionsValue = String(module.details.sessions).trim();
    const durationValue = String(module.details.duration).trim();

    if (!titleValue || !descriptionValue || !sessionsValue || !durationValue) {
      setErrorsById((current) => ({
        ...current,
        [moduleId]: "All fields are required.",
      }));
      return;
    }

    try {
      const response = await updateCourseModule({
        courseId,
        courseModuleId: moduleId,
        payload: {
          moduleName: titleValue,
          moduleDescription: descriptionValue,
          noOfSession: sessionsValue,
          sessionDuration: durationValue,
        },
      }).unwrap();
      setOriginalById((current) => ({
        ...current,
        [moduleId]: {
          title: titleValue,
          description: descriptionValue,
          sessions: sessionsValue,
          duration: durationValue,
        },
      }));
      setErrorsById((current) => {
        if (!current[moduleId]) {
          return current;
        }
        const { [moduleId]: _removed, ...rest } = current;
        return rest;
      });
      showToast({
        message: response?.message || "Course module updated successfully.",
        type: "success",
      });
    } catch (error) {
      const message =
        error?.data?.message || error?.error || "Failed to update module.";
      setErrorsById((current) => ({
        ...current,
        [moduleId]: message,
      }));
      showToast({ message, type: "error" });
    }
  };

  const handleDeleteModule = (moduleId, courseId) => {
    if (!courseId) {
      setModuleItems((current) => current.filter((m) => m.id !== moduleId));
      return;
    }

    (async () => {
      try {
        const response = await deleteCourseModule({
          courseId,
          courseModuleId: moduleId,
        }).unwrap();
        setModuleItems((current) => current.filter((m) => m.id !== moduleId));
        showToast({
          message: response?.message || "Course module deleted successfully.",
          type: "success",
        });
      } catch (error) {
        const message =
          error?.data?.message || error?.error || "Failed to delete module.";
        showToast({ message, type: "error" });
      }
    })();
  };

  const [deleteCourseModule, { isLoading: isDeletingModule }] =
    useDeleteCourseModuleMutation();

  const handleOpenMaterialModal = (moduleId) => {
    setActiveModuleId(moduleId);
    setIsMaterialModalOpen(true);
  };

  const handleCloseMaterialModal = () => {
    if (uploadTimerRef.current) {
      clearInterval(uploadTimerRef.current);
      uploadTimerRef.current = null;
    }

    setIsMaterialModalOpen(false);
    setMaterialFiles([]);
    setMaterialError("");
    setActiveModuleId("");
    setMaterialUploadType("");
    setHasUploadedPdf(false);
    setLastMaterialType("");
  };

  const isAllowedLectureVideo = (file) => {
    const allowedMimeTypes = ["video/mp4", "video/mkv", "video/webm"];
    const lowerName = String(file?.name || "").toLowerCase();
    const extensionAllowed =
      lowerName.endsWith(".mp4") ||
      lowerName.endsWith(".mkv") ||
      lowerName.endsWith(".webm");

    // Backend multer only allows these exact MIME types.
    if (file?.type) {
      return allowedMimeTypes.includes(file.type);
    }

    return extensionAllowed;
  };

  const handleMaterialFileChange = (event) => {
    const selectedFiles = Array.from(event.target.files || []);

    if (selectedFiles.length === 0) {
      return;
    }

    const filteredFiles = selectedFiles.filter((file) => {
      if (materialUploadType === "pdf") {
        return file.type === "application/pdf" || file.name.endsWith(".pdf");
      }

      if (materialUploadType === "video") {
        return isAllowedLectureVideo(file);
      }

      return true;
    });

    if (filteredFiles.length === 0) {
      setMaterialError(
        materialUploadType === "video"
          ? "Only MP4, MKV, and WEBM video files are allowed."
          : "Selected file type is not supported.",
      );
      event.target.value = "";
      return;
    }

    setMaterialError("");

    const nextFiles = filteredFiles.map((file) => {
      const rawSizeInMb = file.size / (1024 * 1024);
      const formattedSize = Number.isInteger(rawSizeInMb)
        ? `${rawSizeInMb.toFixed(0)}mb`
        : `${rawSizeInMb.toFixed(1)}mb`;
      const baseTitle = file.name.replace(/\.[^/.]+$/, "");

      return {
        id: `${file.name}-${file.size}-${Date.now()}-${Math.random()
          .toString(36)
          .slice(2, 8)}`,
        name: file.name,
        file,
        type: materialUploadType || "",
        title: baseTitle,
        sizeLabel: formattedSize,
        progress: 0,
        isUploading: false,
        isComplete: false,
        hasFailed: false,
        errorMessage: "",
      };
    });

    setMaterialFiles((currentFiles) => [...currentFiles, ...nextFiles]);
    event.target.value = "";
    setMaterialUploadType("");
  };

  const handleMaterialTitleChange = (fileId, nextTitle) => {
    setMaterialFiles((currentFiles) =>
      currentFiles.map((file) =>
        file.id === fileId ? { ...file, title: nextTitle } : file,
      ),
    );
  };

  const handleOpenMaterialType = () => {
    const canReuseType =
      (materialFiles.length > 0 || hasUploadedPdf) && lastMaterialType;

    if (canReuseType) {
      setMaterialUploadType(lastMaterialType);
      if (materialInputRef.current) {
        materialInputRef.current.click();
      }
      return;
    }

    setMaterialUploadType((current) => (current ? "" : "chooser"));
  };

  const handleSelectMaterialType = (type) => {
    setMaterialUploadType(type);
    setLastMaterialType(type);
    if (materialInputRef.current) {
      materialInputRef.current.click();
    }
  };

  const handleRemoveMaterialFile = (fileId) => {
    if (uploadTimerRef.current) {
      clearInterval(uploadTimerRef.current);
      uploadTimerRef.current = null;
    }

    setMaterialFiles((currentFiles) =>
      currentFiles.filter((file) => file.id !== fileId),
    );
  };

  const isSelectingFiles = materialFiles.some((file) => file.progress < 100);

  const handleUploadMaterial = async () => {
    if (!courseId || !activeModuleId) {
      setMaterialError("Course or module information is missing.");
      return;
    }

    if (materialFiles.length === 0) {
      setMaterialError("Please select at least one file to upload.");
      return;
    }

    const hasEmptyTitle = materialFiles.some((file) => !file.title.trim());
    if (hasEmptyTitle) {
      setMaterialError("Please enter a title for each file.");
      return;
    }
    if (isUploadingMaterials || isSelectingFiles) {
      return;
    }

    const getUploadErrorMessage = (error) => {
      const isPayloadTooLarge =
        error?.status === 413 ||
        error?.originalStatus === 413 ||
        error?.status === "FETCH_ERROR" ||
        /failed to fetch/i.test(error?.error || error?.message || "");

      return (
        error?.data?.message ||
        (isPayloadTooLarge
          ? "File is too large to upload. Please try a smaller file."
          : error?.error || "Failed to upload file.")
      );
    };

    setMaterialError("");
    setIsUploadingMaterials(true);

    let successCount = 0;
    let failureCount = 0;

    for (const file of materialFiles) {
      setMaterialFiles((currentFiles) =>
        currentFiles.map((item) =>
          item.id === file.id
            ? {
                ...item,
                isUploading: true,
                isComplete: false,
                hasFailed: false,
                errorMessage: "",
                progress: Math.max(item.progress, 10),
              }
            : item,
        ),
      );

      try {
        if (file.type === "video" && !isAllowedLectureVideo(file.file)) {
          throw {
            data: {
              message: "Only MP4, MKV, and WEBM video files are allowed.",
            },
          };
        }

        const formData = new FormData();
        formData.append("title", file.title.trim());
        formData.append("courseId", courseId);
        formData.append("moduleId", activeModuleId);

        if (file.type === "video") {
          // Backend expects multipart field name: "lecture"
          formData.append("lecture", file.file, file.file.name);
          await uploadCourseLecture({ courseId, formData }).unwrap();
        } else {
          formData.append("pdf", file.file);
          await uploadCoursePdf({ courseId, formData }).unwrap();
        }

        successCount += 1;
        // Uploaded successfully — remove it from the list immediately.
        setMaterialFiles((currentFiles) =>
          currentFiles.filter((item) => item.id !== file.id),
        );
      } catch (error) {
        failureCount += 1;
        const message = getUploadErrorMessage(error);
        setMaterialFiles((currentFiles) =>
          currentFiles.map((item) =>
            item.id === file.id
              ? {
                  ...item,
                  isUploading: false,
                  isComplete: false,
                  hasFailed: true,
                  errorMessage: message,
                }
              : item,
          ),
        );
        // Continue with the remaining files instead of stopping the batch.
      }
    }

    if (successCount > 0) {
      setHasUploadedPdf(true);
    }

    if (failureCount > 0) {
      setMaterialError(
        successCount > 0
          ? `${successCount} file(s) uploaded successfully. ${failureCount} file(s) failed — see details below.`
          : `${failureCount} file(s) failed to upload — see details below.`,
      );
    }

    setIsUploadingMaterials(false);
  };

  useEffect(() => {
    if (materialFiles.length === 0) {
      return undefined;
    }

    if (uploadTimerRef.current) {
      clearInterval(uploadTimerRef.current);
    }

    uploadTimerRef.current = setInterval(() => {
      setMaterialFiles((currentFiles) => {
        const updatedFiles = currentFiles.map((file) => {
          if (file.progress >= 100) {
            return file;
          }

          const nextProgress = Math.min(file.progress + 2, 100);

          return {
            ...file,
            progress: nextProgress,
            isComplete: nextProgress === 100,
          };
        });

        const allComplete =
          updatedFiles.length > 0 &&
          updatedFiles.every((file) => file.progress === 100);

        if (allComplete && uploadTimerRef.current) {
          clearInterval(uploadTimerRef.current);
          uploadTimerRef.current = null;
        }

        return updatedFiles;
      });
    }, 70);

    return () => {
      if (uploadTimerRef.current) {
        clearInterval(uploadTimerRef.current);
        uploadTimerRef.current = null;
      }
    };
  }, [materialFiles.length]);

  useEffect(() => {
    return () => {
      if (uploadTimerRef.current) {
        clearInterval(uploadTimerRef.current);
      }
    };
  }, []);

  const completedModulesCount = moduleItems.filter(
    (module) => getModuleStatus(module).label === "Complete",
  ).length;
  const incompleteModulesCount = moduleItems.length - completedModulesCount;

  const getOverallStatus = () => {
    if (moduleItems.length === 0) {
      return {
        label: "Incomplete",
        className: "bg-[#FEE2E2] text-[#991B1B]",
      };
    }

    if (moduleItems.length > 1 && incompleteModulesCount > 0) {
      return {
        label: "Partially Complete",
        className: "bg-[#FEF9C3] text-[#854D0E]",
      };
    }

    if (completedModulesCount > 0) {
      return {
        label: "Complete",
        className: "bg-[#DCFCE7] text-[#166534]",
      };
    }

    return {
      label: "Incomplete",
      className: "bg-[#FEE2E2] text-[#991B1B]",
    };
  };

  const overallStatus = getOverallStatus();

  return (
    <section className="mt-6 overflow-hidden rounded-xl border border-[#E5E7EB] bg-white shadow-[0_1px_2px_#0000000D]">
      <header className="flex flex-col gap-3 border-b border-[#E5E7EB] bg-[#F9FAFB] px-4 py-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:px-6">
        <div className="flex flex-wrap items-center gap-3">
          <h3 className="text-base text-[#1F2937] sm:text-lg">
            Course Modules
          </h3>
          <span
            className={`rounded-full px-2.5 py-0.5 text-xs ${overallStatus.className}`}
          >
            {overallStatus.label}
          </span>
        </div>

        <span
          className={`rounded-full px-2.5 py-0.5 text-xs ${
            moduleItems.length >= 1
              ? "bg-[#ECFDF5] text-[#065F46]"
              : "bg-[#FEE2E2] text-[#991B1B]"
          }`}
        >
          {moduleItems.length >= 1 ? "Ready to publish" : "Minimum 1 required"}
        </span>
      </header>

      <div className="space-y-6 px-4 pb-8 pt-6 sm:px-6 sm:pb-10">
        {moduleItems.map((module, idx) => {
          const moduleStatus = getModuleStatus(module);
          const isNewModule = module.isNew;
          const original = originalById[module.id] || {};
          const hasChanges =
            !isNewModule &&
            (module.title.trim() !== (original.title || "").trim() ||
              module.details.description.trim() !==
                (original.description || "").trim() ||
              String(module.details.sessions).trim() !==
                String(original.sessions || "").trim() ||
              String(module.details.duration).trim() !==
                String(original.duration || "").trim());
          const errorMessage = errorsById[module.id];

          return (
            <article
              key={module.id}
              className="overflow-hidden rounded-xl border border-[#E5E7EB] bg-white"
            >
              <div className="flex flex-col gap-4 border-b border-[#E5E7EB] bg-[#F9FAFB] p-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#923D8C] text-xs  text-white">
                    {idx + 1}
                  </span>

                  <input
                    type="text"
                    value={module.title}
                    onChange={(event) =>
                      handleTitleChange(module.id, event.target.value)
                    }
                    placeholder="Type module name"
                    className="h-[38px] min-w-0 flex-1 rounded-lg border border-[#D1D5DB] bg-white px-3 text-sm text-[#111827] outline-none sm:max-w-[350px] sm:text-base"
                  />

                  <span
                    className={`rounded-full px-2.5 py-0.5 text-xs ${moduleStatus.className}`}
                  >
                    {moduleStatus.label}
                  </span>
                </div>

                <div className="flex items-center justify-end gap-4 text-[#6B7280] sm:justify-normal">
                  <button
                    type="button"
                    aria-label={
                      module.isExpanded ? "Collapse module" : "Expand module"
                    }
                    onClick={() => handleModuleToggle(module.id)}
                    className="transition hover:text-[#374151]"
                  >
                    {module.isExpanded ? (
                      <ChevronUp size={18} />
                    ) : (
                      <ChevronDown size={18} />
                    )}
                  </button>
                  {!isNewModule ? (
                    <>
                      <button
                        type="button"
                        aria-label="Delete module"
                        onClick={() =>
                          handleDeleteModule(module.id, module.courseId)
                        }
                        className="transition hover:text-[#374151]"
                      >
                        <Trash2 size={17} />
                      </button>
                      <button
                        type="button"
                        aria-label="Edit module"
                        onClick={() => handleModuleToggle(module.id)}
                        className="transition hover:text-[#374151]"
                      >
                        <Pencil size={17} />
                      </button>
                    </>
                  ) : null}
                </div>
              </div>

              {module.isExpanded && (
                <div className="grid grid-cols-1 gap-4 p-4 sm:gap-6 md:grid-cols-2 xl:grid-cols-[1.9fr_1fr_1fr_auto] xl:items-start">
                  <div>
                    <p className="mb-1 flex items-center gap-1 text-sm text-[#374151]">
                      <span>Module Description</span>
                      <Check size={14} className="text-[#16A34A]" />
                    </p>

                    <textarea
                      value={module.details.description}
                      onChange={(event) =>
                        handleDetailChange(
                          module.id,
                          "description",
                          event.target.value,
                        )
                      }
                      placeholder="Type module description"
                      className="min-h-[90px] w-full resize-none rounded-lg border border-[#D1D5DB] bg-white px-3 py-2 text-sm text-[#000] outline-none sm:text-base"
                    />
                  </div>

                  <div className="min-w-0">
                    <label className="mb-1 flex items-center gap-1 text-sm text-[#374151]">
                      <span>Number of Sessions</span>
                      <Check size={14} className="text-[#16A34A]" />
                    </label>

                    <input
                      type="number"
                      min="0"
                      value={module.details.sessions}
                      onChange={(event) =>
                        handleDetailChange(
                          module.id,
                          "sessions",
                          event.target.value,
                        )
                      }
                      placeholder="0"
                      className="h-[42px] w-full min-w-[182px] rounded-lg border border-[#D1D5DB] bg-white px-3 text-sm text-[#000] outline-none xl:max-w-[182px] sm:text-base"
                    />
                  </div>

                  <div>
                    <label className="mb-1 flex items-center gap-1 text-sm text-[#374151]">
                      <span>Session Duration</span>
                      <Check size={14} className="text-[#16A34A]" />
                    </label>

                    <div className="flex h-[42px] items-center overflow-hidden rounded-lg border border-[#D1D5DB] bg-white">
                      <input
                        type="number"
                        min="0"
                        value={module.details.duration}
                        onChange={(event) =>
                          handleDetailChange(
                            module.id,
                            "duration",
                            event.target.value,
                          )
                        }
                        placeholder="0"
                        className="h-full w-full bg-transparent px-3 text-sm text-[#000] outline-none sm:text-base"
                      />
                      <span className="inline-flex h-full items-center border-l border-[#E5E7EB] bg-[#F9FAFB] px-3 text-sm text-[#6B7280] sm:text-base">
                        minutes
                      </span>
                    </div>
                  </div>

                  <div className="pt-0 xl:pt-6">
                    {module.isNew ? (
                      <div className="flex items-center gap-2 mt-6 xl:mt-0">
                        <button
                          type="button"
                          onClick={() => handleSaveModule(module.id)}
                          className="inline-flex h-10 xl:h-[42px] flex-1 items-center justify-center rounded border border-[#D1D5DB] bg-white px-4 text-sm text-[#374151] transition hover:bg-[#F9FAFB] sm:flex-none"
                        >
                          Save
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setModuleItems((current) =>
                              current.filter((item) => item.id !== module.id),
                            )
                          }
                          className="inline-flex h-10 xl:h-[42px] flex-1 items-center justify-center rounded border border-[#D1D5DB] bg-white px-4 text-sm text-[#374151] transition hover:bg-[#F9FAFB] sm:flex-none"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ) : hasChanges ? (
                      <button
                        type="button"
                        onClick={() => handleUpdateModule(module.id)}
                        className="inline-flex h-[42px] w-full items-center justify-center rounded border border-[#D1D5DB] bg-white px-4 text-sm text-[#374151] transition hover:bg-[#F9FAFB] sm:w-auto"
                      >
                        Update
                      </button>
                    ) : !isLiveCourse ? (
                      <button
                        type="button"
                        onClick={() => handleOpenMaterialModal(module.id)}
                        className="inline-flex h-[42px] w-full items-center justify-center gap-1 rounded border border-[#D1D5DB] bg-white px-3 text-sm text-[#374151] transition hover:bg-[#F9FAFB] sm:w-auto"
                      >
                        <Plus size={16} />
                        <span>Add Material</span>
                      </button>
                    ) : null}
                  </div>
                </div>
              )}
              {errorMessage ? (
                <p className="px-4 pb-4 text-xs text-[#DC2626]">
                  {errorMessage}
                </p>
              ) : null}
            </article>
          );
        })}
        <button
          type="button"
          onClick={handleAddModule}
          className="mt-6 inline-flex h-[38px] w-full items-center justify-center rounded border border-[#D1D5DB] bg-white px-4 text-sm text-[#374151] sm:w-auto"
        >
          + Add Module
        </button>
      </div>

      {isMaterialModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0F172AB3] px-3 py-4 sm:p-4">
          <div className="max-h-[min(94vh,calc(100dvh-1.5rem))] w-full max-w-[1036px] overflow-hidden rounded-[10px] bg-white">
            <div className="flex items-center justify-between gap-4 border-b border-[#00000080] px-4 py-5 sm:px-10 sm:py-8">
              <div>
                <h4 className="text-xl font-bold leading-7 text-[#000] sm:text-[28px] sm:leading-[33px]">
                  Upload File
                </h4>
              </div>

              <button
                type="button"
                aria-label="Close upload modal"
                onClick={handleCloseMaterialModal}
                className="text-[#000] transition hover:opacity-70"
              >
                <X size={30} strokeWidth={1.5} />
              </button>
            </div>

            <div className="max-h-[calc(100dvh-8rem)] overflow-y-auto px-4 pb-6 pt-6 sm:px-12 sm:pb-14 sm:pt-10">
              <div className="rounded-lg border border-dashed border-[#00000080] p-4">
                <div
                  className={`flex min-h-[267px] ${
                    materialFiles.length > 0
                      ? "flex-col justify-between"
                      : "items-center justify-center"
                  }`}
                >
                  {materialFiles.length > 0 ? (
                    <div className="grid w-full gap-3 sm:grid-cols-2 xl:grid-cols-3">
                      {materialFiles.map((file) => (
                        <div
                          key={file.id}
                          className="rounded bg-[#EEEEEE] px-2.5 py-2.5"
                        >
                          <div className="flex items-center justify-between gap-4">
                            <div className="flex min-w-0 flex-1 items-center gap-2">
                              <span className="inline-flex h-5 w-5 items-center justify-center rounded-[5px] bg-[#923D8C1A]">
                                <Player />
                              </span>

                              <div className="min-w-0 flex-1">
                                <div className="flex items-center justify-between gap-3">
                                  <p className="truncate text-sm text-[#374151] sm:text-base">
                                    {file.name} ({file.sizeLabel})
                                  </p>
                                  {file.isUploading ? (
                                    <span className="inline-flex items-center gap-2 text-xs text-[#6B7280] sm:text-sm">
                                      <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-[#923D8C] border-t-transparent" />
                                      Uploading...
                                    </span>
                                  ) : file.isComplete ? (
                                    <span className="inline-flex items-center gap-1 text-xs text-[#16A34A] sm:text-sm">
                                      <Check size={14} />
                                      Done
                                    </span>
                                  ) : file.hasFailed ? (
                                    <span className="inline-flex items-center gap-1 text-xs text-[#DC2626] sm:text-sm">
                                      <X size={14} />
                                      Failed
                                    </span>
                                  ) : null}
                                </div>

                                <div className="mt-1 h-2.5 w-full overflow-hidden bg-[#fff]">
                                  <div
                                    className={`h-full transition-[width] duration-100 ease-linear ${
                                      file.hasFailed
                                        ? "bg-[#DC2626]"
                                        : "bg-[#1ECB5E]"
                                    }`}
                                    style={{ width: `${file.progress}%` }}
                                  />
                                </div>

                                {file.hasFailed && file.errorMessage ? (
                                  <p className="mt-1 text-xs text-[#DC2626]">
                                    {file.errorMessage}
                                  </p>
                                ) : null}

                                {file.type === "pdf" ? (
                                  <input
                                    type="text"
                                    placeholder="Title"
                                    value={file.title}
                                    onChange={(event) =>
                                      handleMaterialTitleChange(
                                        file.id,
                                        event.target.value,
                                      )
                                    }
                                    className="mt-2 h-8 w-full rounded border border-[#D1D5DB] bg-white px-2 text-sm text-[#111827] outline-none"
                                  />
                                ) : null}
                              </div>
                            </div>

                            <button
                              type="button"
                              aria-label={`Remove ${file.name}`}
                              onClick={() => handleRemoveMaterialFile(file.id)}
                              className="text-black transition hover:opacity-70"
                            >
                              <X size={18} strokeWidth={2} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : null}

                  <div
                    className={
                      materialFiles.length > 0
                        ? "mt-4 text-left"
                        : "text-center"
                    }
                  >
                    <input
                      ref={materialInputRef}
                      id="material-upload-input"
                      type="file"
                      multiple
                      className="hidden"
                      accept={
                        materialUploadType === "pdf"
                          ? ".pdf"
                          : materialUploadType === "video"
                            ? ".mp4,.mkv,.webm,video/mp4,video/mkv,video/webm"
                            : ".mp4,.mkv,.webm,.pdf"
                      }
                      onChange={handleMaterialFileChange}
                    />

                    <div className="relative inline-flex w-full flex-col items-start gap-3 sm:w-auto">
                      <button
                        type="button"
                        onClick={handleOpenMaterialType}
                        className="inline-flex w-full cursor-pointer items-center justify-center gap-1.5 rounded bg-[#923D8C] px-4 py-2 text-base font-medium text-white sm:w-auto sm:text-lg"
                      >
                        <Paperclip size={20} />
                        <span>
                          {materialFiles.length > 0 || hasUploadedPdf
                            ? "Attach More Files"
                            : "Attach File"}
                        </span>
                      </button>

                      {materialUploadType === "chooser" && (
                        <div className="flex items-center gap-4">
                          <button
                            type="button"
                            onClick={() => handleSelectMaterialType("video")}
                            className="inline-flex h-[58px] w-[58px] items-center justify-center rounded-lg border border-[#D1D5DB] bg-white"
                            aria-label="Select video"
                          >
                            <VideoTypeIcon />
                          </button>

                          <button
                            type="button"
                            onClick={() => handleSelectMaterialType("pdf")}
                            className="inline-flex h-[58px] w-[58px] items-center justify-center rounded-lg border border-[#D1D5DB] bg-white"
                            aria-label="Select PDF"
                          >
                            <PdfTypeIcon />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
                <p className="text-sm font-medium text-[#9C9C9C] sm:text-base">
                  Supported Formats: Video (MP4, MKV, WEBM), PDF
                </p>

                <div className="flex flex-col-reverse gap-2 font-['Inter'] sm:flex-row sm:items-center">
                  <button
                    type="button"
                    onClick={handleCloseMaterialModal}
                    className="inline-flex h-10 items-center justify-center rounded border border-[#6B7280B2] bg-white px-7 text-base text-[#4A4A68]"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleUploadMaterial}
                    disabled={
                      isUploadingPdf ||
                      isUploadingLecture ||
                      isUploadingMaterials ||
                      isSelectingFiles
                    }
                    className={`inline-flex h-10 items-center justify-center gap-1.5 rounded bg-[#923D8C] px-4 text-base text-white transition-opacity ${
                      isUploadingPdf ||
                      isUploadingLecture ||
                      isUploadingMaterials ||
                      isSelectingFiles
                        ? "cursor-not-allowed opacity-50"
                        : "hover:opacity-90"
                    }`}
                  >
                    <Upload size={18} />
                    <span>{isUploadingMaterials ? "Uploading..." : "Upload"}</span>
                  </button>
                </div>
              </div>

              {materialError ? (
                <div className="mt-4 rounded-lg border border-[#FCA5A5] bg-[#FEF2F2] px-4 py-3 text-sm text-[#991B1B]">
                  {materialError}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {(isDeletingModule || isCreatingModule || isUpdatingModule) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
          <div className="relative z-10">
            <Loader size="lg" />
          </div>
        </div>
      )}
    </section>
  );
};

export default CourseModulesSection;
